/**
 * Intérprete de patrones de formato de Excel (los mismos códigos que se escriben
 * en "Formato de celdas → Personalizada"), usado por la página Configuración.
 *
 * Pattern convention: patterns are STORED canonically (`.` decimal, `,` thousands — Excel's own
 * internal convention) and RENDERED with the separators configured in `ajustes.separador_decimal`.
 * With the default (`,` decimal), the stored pattern `$#,##0.00` produces `$39.802,39`. The
 * Settings page translates at the edges with `patronADisplay` / `patronACanonico`, so the user
 * still writes `$#.##0,00`. Date patterns carry no separators and are unaffected.
 *
 * Soportado
 *   Números: secciones `positivo;negativo;cero`, marcadores `#`, `0`, `?`,
 *            separador de miles, decimales, `%` (escala ×100), literales entre
 *            comillas, `\x`, `_x` (espacio), `*x` (se ignora el relleno) y
 *            colores por sección: `[Rojo]` / `[Red]`, `[Verde]`, `[Azul]`…
 *            (usar `formatearExcelConColor` para obtenerlo).
 *   Fechas:  `yyyy` `yy` `mmmm` `mmm` `mm` `m` `dddd` `ddd` `dd` `d`
 *            `hh` `h` `ss` `s` `AM/PM` `a/p`, con nombres en español.
 *            `m`/`mm` valen minutos si siguen a una hora o preceden a segundos.
 *
 * No soportado (se ignora o se trata como literal): fracciones `# ?/?`,
 * notación científica, condiciones `[>100]` y horas transcurridas `[h]`.
 */

// ── Separators ────────────────────────────────────────────────────────────────────────────────
//
// Patterns are ALWAYS parsed in canonical convention — `.` decimal and `,` thousands, which is
// what Excel uses internally — and rendered with whatever separators the user configured
// (`ajustes.separador_decimal`). Storing patterns canonically is what keeps flipping that setting
// from silently changing the meaning of every stored pattern: `$#.##0,00` and `$#,##0.00` are the
// same pattern in two conventions, and only one of them can be the stored form.
//
// The Settings page shows and accepts patterns in the USER's convention, translating at the edges
// with `patronADisplay` / `patronACanonico`.

const CANON_DECIMAL = '.'
const CANON_MILES = ','

let SEP_DECIMAL: '.' | ',' = ','
let SEP_MILES: '.' | ',' = '.'

/** Sets the separators used to RENDER numbers. The thousands separator is the other one, as in
 *  every locale Excel ships. Called from `ConfigContext` when `ajustes` loads. */
export function establecerSeparadores(decimal: '.' | ','): void {
  SEP_DECIMAL = decimal
  SEP_MILES = decimal === ',' ? '.' : ','
}

export function obtenerSeparadorDecimal(): '.' | ',' {
  return SEP_DECIMAL
}

/**
 * Swaps the decimal and thousands separators of a pattern. Three kinds of content are left
 * untouched, because a `.` or `,` there is not a separator:
 *   - quoted literals (`"1.000 u."`),
 *   - `\x` escapes,
 *   - anything between brackets — colours (`[Rojo]`) and, more to the point, conditions
 *     (`[>1.000]`), where swapping would change the number being compared.
 * With `decimal === '.'` the two conventions coincide and nothing changes.
 */
function cambiarConvencion(patron: string, desde: string, hasta: string, desdeMiles: string, hastaMiles: string): string {
  if (desde === hasta) return patron
  let salida = ''
  let enComillas = false
  let enCorchetes = false
  for (let i = 0; i < patron.length; i++) {
    const c = patron[i]
    if (c === '\\') { salida += c + (patron[i + 1] ?? ''); i++; continue }
    if (c === '"' && !enCorchetes) { enComillas = !enComillas; salida += c; continue }
    if (!enComillas && (c === '[' || c === ']')) { enCorchetes = c === '['; salida += c; continue }
    if (enComillas || enCorchetes) { salida += c; continue }
    if (c === desde) { salida += hasta; continue }
    if (c === desdeMiles) { salida += hastaMiles; continue }
    salida += c
  }
  return salida
}

/** Pattern as the user writes it (their decimal separator) → stored canonical form. */
export function patronACanonico(patron: string, decimalUsuario: '.' | ','): string {
  const milesUsuario = decimalUsuario === ',' ? '.' : ','
  return cambiarConvencion(patron, decimalUsuario, CANON_DECIMAL, milesUsuario, CANON_MILES)
}

/** Stored canonical pattern → what the user reads and edits in Settings. */
export function patronADisplay(patron: string, decimalUsuario: '.' | ','): string {
  const milesUsuario = decimalUsuario === ',' ? '.' : ','
  return cambiarConvencion(patron, CANON_DECIMAL, decimalUsuario, CANON_MILES, milesUsuario)
}

let LOCALE_FORMATO = 'es-AR'

export function establecerLocaleFormato(localeTag: string): void {
  LOCALE_FORMATO = localeTag
}

export function obtenerLocaleFormato(): string {
  return LOCALE_FORMATO
}

// ── Nombres de día y mes según el idioma activo (los provee Intl; se normalizan a minúscula y sin punto) ──
const nombre = (opciones: Intl.DateTimeFormatOptions, d: Date, localeOverride?: string) =>
  new Intl.DateTimeFormat(localeOverride || LOCALE_FORMATO, opciones).format(d).replace(/\.$/, '').toLowerCase()

const diaLargo = (d: Date, locale?: string) => nombre({ weekday: 'long' }, d, locale)
const diaCorto = (d: Date, locale?: string) => nombre({ weekday: 'short' }, d, locale).slice(0, 3)
const mesLargo = (d: Date, locale?: string) => nombre({ month: 'long' }, d, locale)
const mesCorto = (d: Date, locale?: string) => nombre({ month: 'short' }, d, locale).slice(0, 3)

/** Divide el patrón en secciones por `;`, respetando comillas y escapes. */
export function dividirSecciones(patron: string): string[] {
  const secciones: string[] = []
  let actual = ''
  let enComillas = false
  for (let i = 0; i < patron.length; i++) {
    const c = patron[i]
    if (c === '\\') { actual += c + (patron[i + 1] ?? ''); i++; continue }
    if (c === '"') { enComillas = !enComillas; actual += c; continue }
    if (c === ';' && !enComillas) { secciones.push(actual); actual = ''; continue }
    actual += c
  }
  secciones.push(actual)
  return secciones
}

/** Quita corchetes de color/condición (`[Rojo]`, `[>100]`), que no afectan al texto. */
const sinCorchetes = (s: string) => s.replace(/\[[^\]]*\]/g, '')

// Colores de fuente que Excel admite entre corchetes, en español e inglés.
// El valor es un token CSS: los que existen como variable del tema la usan, para
// que el rojo del sistema sea el mismo en modo claro y oscuro.
const COLORES: Record<string, string> = {
  rojo: 'var(--danger)', red: 'var(--danger)',
  verde: 'var(--success)', green: 'var(--success)',
  azul: '#2563EB', blue: '#2563EB',
  negro: 'var(--text)', black: 'var(--text)',
  blanco: '#FFFFFF', white: '#FFFFFF',
  amarillo: '#CA8A04', yellow: '#CA8A04',
  magenta: '#C026D3',
  cian: '#0891B2', cyan: '#0891B2',
}

/** Color declarado entre corchetes en la sección (`[Rojo]-#.##0`), o `undefined`. */
function colorDeSeccion(seccion: string): string | undefined {
  for (const m of seccion.matchAll(/\[([^\]]*)\]/g)) {
    const color = COLORES[(m[1] ?? '').trim().toLowerCase()]
    if (color) return color
  }
  return undefined
}

/** `true` si el patrón describe una fecha/hora (y no un número). */
export function esFormatoFecha(patron: string): boolean {
  const s = sinCorchetes(dividirSecciones(patron)[0] ?? '')
  let enComillas = false
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i)
    if (c === '\\') { i++; continue }
    if (c === '"') { enComillas = !enComillas; continue }
    if (enComillas) continue
    if ('ymdhs'.includes(c.toLowerCase())) return true
    if (c === '#' || c === '0' || c === '?') return false
  }
  return false
}

// ── Tokenizado común ──

type Token =
  | { t: 'lit'; v: string }
  | { t: 'ph'; v: '#' | '0' | '?' }   // marcador de dígito
  | { t: 'dec' }                       // separador decimal
  | { t: 'miles' }                     // separador de miles
  | { t: 'pct' }                       // porcentaje
  | { t: 'fecha'; v: string }          // token de fecha/hora (yyyy, mmm, ...)

/** Devuelve el token de fecha que empieza en `i`, o `null`. */
function tokenFecha(s: string, i: number): string | null {
  const resto = s.slice(i)
  const m = /^(am\/pm|a\/p|yyyy|yyy|yy|y|mmmmm|mmmm|mmm|mm|m|dddd|ddd|dd|d|hh|h|ss|s)/i.exec(resto)
  return m ? m[0] : null
}

function tokenizar(seccion: string, modoFecha: boolean): Token[] {
  const s = sinCorchetes(seccion)
  const tokens: Token[] = []
  const literal = (v: string) => {
    const ultimo = tokens[tokens.length - 1]
    if (ultimo && ultimo.t === 'lit') ultimo.v += v
    else tokens.push({ t: 'lit', v })
  }

  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i)
    if (c === '\\') { literal(s[i + 1] ?? ''); i++; continue }
    if (c === '"') {
      const fin = s.indexOf('"', i + 1)
      literal(s.slice(i + 1, fin === -1 ? undefined : fin))
      i = fin === -1 ? s.length : fin
      continue
    }
    // `_x` reserva el ancho de x → un espacio; `*x` rellena → se ignora.
    if (c === '_') { literal(' '); i++; continue }
    if (c === '*') { i++; continue }

    if (modoFecha) {
      const tk = tokenFecha(s, i)
      if (tk) { tokens.push({ t: 'fecha', v: tk }); i += tk.length - 1; continue }
      literal(c ?? '')
      continue
    }

    if (c === '#' || c === '0' || c === '?') { tokens.push({ t: 'ph', v: c }); continue }
    if (c === '%') { tokens.push({ t: 'pct' }); continue }
    // The PATTERN is read in canonical convention; only the OUTPUT uses the configured separators.
    if (c === CANON_DECIMAL) { tokens.push({ t: 'dec' }); continue }
    if (c === CANON_MILES) {
      // Es separador de miles solo entre marcadores de dígito; si no, es literal.
      const previo = tokens[tokens.length - 1]
      const siguiente = s[i + 1]
      if (previo?.t === 'ph' && siguiente && '#0?'.includes(siguiente)) { tokens.push({ t: 'miles' }); continue }
      literal(c ?? '')
      continue
    }
    literal(c ?? '')
  }
  return tokens
}

// ── Números ──

/** Inserta el separador de miles cada 3 dígitos. */
function agrupar(digitos: string): string {
  return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, SEP_MILES)
}

function formatearNumeroSeccion(valor: number, seccion: string): string {
  const tokens = tokenizar(seccion, false)

  const escalas = tokens.filter((t) => t.t === 'pct').length
  let n = valor * Math.pow(100, escalas)

  const idxDec = tokens.findIndex((t) => t.t === 'dec')
  const phEnteros = tokens.filter((t, i) => t.t === 'ph' && (idxDec === -1 || i < idxDec)) as { t: 'ph'; v: string }[]
  const phDecimales = tokens.filter((t, i) => t.t === 'ph' && idxDec !== -1 && i > idxDec) as { t: 'ph'; v: string }[]
  const agrupaMiles = tokens.some((t) => t.t === 'miles')

  // Sin ningún marcador de dígito la sección es puro literal (ej. la sección de cero `-`).
  if (phEnteros.length === 0 && phDecimales.length === 0) {
    return tokens.map((t) => (t.t === 'lit' ? t.v : t.t === 'pct' ? '%' : '')).join('')
  }

  n = Math.abs(n)
  const fijo = n.toFixed(phDecimales.length)
  const [entero, decimalesFijos = ''] = fijo.split('.')
  // Los `#` decimales son opcionales: se quitan los ceros sobrantes a la derecha.
  const minDecimales = phDecimales.filter((p) => p.v !== '#').length
  let decimales = decimalesFijos
  while (decimales.length > minDecimales && decimales.endsWith('0')) decimales = decimales.slice(0, -1)

  // Parte entera: rellenar hasta la cantidad de marcadores obligatorios (`0`/`?`).
  const minEnteros = phEnteros.filter((p) => p.v !== '#').length
  let digitosEnteros = entero ?? ''
  if (digitosEnteros === '0' && minEnteros === 0) digitosEnteros = ''
  digitosEnteros = digitosEnteros.padStart(minEnteros, '0')
  // El relleno de `?` es espacio en blanco, no cero.
  const huecos = phEnteros.filter((p) => p.v === '?').length
  if (huecos > 0 && digitosEnteros.length < phEnteros.length) {
    digitosEnteros = digitosEnteros.padStart(Math.min(phEnteros.length, digitosEnteros.length + huecos), ' ')
  }

  // ¿Los marcadores enteros forman un único bloque contiguo? (caso habitual:
  // `#.##0`). Si hay literales en el medio (`0000 - 00000000`), Excel reparte
  // los dígitos de derecha a izquierda, un dígito por marcador.
  const posiciones = tokens.map((t, i) => ({ t, i })).filter(({ t }) => t.t === 'ph' || t.t === 'miles')
  const enterosIdx = posiciones.filter(({ i }) => idxDec === -1 || i < idxDec).map(({ i }) => i)
  const contiguo = enterosIdx.every((idx, k) => k === 0 || idx === (enterosIdx[k - 1] ?? -2) + 1)

  const salida: string[] = []
  if (contiguo) {
    const bloque = agrupaMiles ? agrupar(digitosEnteros.trimStart()).padStart(digitosEnteros.length, ' ') : digitosEnteros
    let bloqueEmitido = false
    let iDec = 0
    for (let i = 0; i < tokens.length; i++) {
      const tk = tokens[i]
      if (tk === undefined) continue
      if (tk.t === 'lit') { salida.push(tk.v); continue }
      if (tk.t === 'pct') { salida.push('%'); continue }
      if (tk.t === 'dec') { salida.push(decimales.length > 0 ? SEP_DECIMAL : ''); continue }
      if (tk.t === 'miles') continue
      // marcador de dígito
      if (idxDec === -1 || i < idxDec) {
        if (!bloqueEmitido) { salida.push(bloque); bloqueEmitido = true }
      } else {
        salida.push(decimales[iDec] ?? '')
        iDec++
      }
    }
  } else {
    // Reparto de derecha a izquierda entre los marcadores enteros.
    const trozos = new Array<string>(phEnteros.length).fill('')
    let resto = digitosEnteros
    for (let k = phEnteros.length - 1; k >= 0; k--) {
      if (k === 0) { trozos[0] = resto; break }
      trozos[k] = resto.slice(-1)
      resto = resto.slice(0, -1)
      if (resto === '') resto = ''
    }
    let kEnteros = 0
    let iDec = 0
    for (let i = 0; i < tokens.length; i++) {
      const tk = tokens[i]
      if (tk === undefined) continue
      if (tk.t === 'lit') { salida.push(tk.v); continue }
      if (tk.t === 'pct') { salida.push('%'); continue }
      if (tk.t === 'dec') { salida.push(decimales.length > 0 ? SEP_DECIMAL : ''); continue }
      if (tk.t === 'miles') continue
      if (idxDec === -1 || i < idxDec) { salida.push(trozos[kEnteros] ?? ''); kEnteros++ }
      else { salida.push(decimales[iDec] ?? ''); iDec++ }
    }
  }
  return salida.join('')
}

// ── Fechas ──

function formatearFechaSeccion(d: Date, seccion: string): string {
  const tokens = tokenizar(seccion, true)
  const hay12h = tokens.some((t) => t.t === 'fecha' && /^(am\/pm|a\/p)$/i.test(t.v))
  const p2 = (n: number) => String(n).padStart(2, '0')

  // `m`/`mm` son minutos si vienen después de una hora o antes de segundos.
  const esMinuto = (i: number): boolean => {
    for (let k = i - 1; k >= 0; k--) {
      const t = tokens[k]
      if (t === undefined) break
      if (t.t === 'fecha') return /^h+$/i.test(t.v)
      if (t.t === 'lit' && /[a-z]/i.test(t.v)) return false
    }
    for (let k = i + 1; k < tokens.length; k++) {
      const t = tokens[k]
      if (t === undefined) break
      if (t.t === 'fecha') return /^s+$/i.test(t.v)
      if (t.t === 'lit' && /[a-z]/i.test(t.v)) return false
    }
    return false
  }

  const horas12 = () => (d.getHours() % 12 === 0 ? 12 : d.getHours() % 12)

  return tokens
    .map((tk, i) => {
      if (tk.t !== 'fecha') return tk.t === 'lit' ? tk.v : ''
      const v = tk.v.toLowerCase()
      switch (v) {
        case 'yyyy': case 'yyy': return String(d.getFullYear())
        case 'yy': case 'y': return p2(d.getFullYear() % 100)
        case 'mmmmm': return mesLargo(d).charAt(0)
        case 'mmmm': return mesLargo(d)
        case 'mmm': return mesCorto(d)
        case 'mm': return esMinuto(i) ? p2(d.getMinutes()) : p2(d.getMonth() + 1)
        case 'm': return esMinuto(i) ? String(d.getMinutes()) : String(d.getMonth() + 1)
        case 'dddd': return diaLargo(d)
        case 'ddd': return diaCorto(d)
        case 'dd': return p2(d.getDate())
        case 'd': return String(d.getDate())
        case 'hh': return p2(hay12h ? horas12() : d.getHours())
        case 'h': return String(hay12h ? horas12() : d.getHours())
        case 'ss': return p2(d.getSeconds())
        case 's': return String(d.getSeconds())
        case 'am/pm': return d.getHours() < 12 ? 'AM' : 'PM'
        case 'a/p': return d.getHours() < 12 ? 'a' : 'p'
        default: return ''
      }
    })
    .join('')
}

// ── Lectura (texto que el usuario escribe → número) ──

/** Cuántas veces escala ×100 la primera sección del patrón (los `%` que tenga). */
function escalasDe(patron: string): number {
  if (!patron.trim() || esFormatoFecha(patron)) return 0
  return tokenizar(dividirSecciones(patron)[0] ?? '', false).filter((t) => t.t === 'pct').length
}

/** El número tal cual, con el separador decimal configurado y sin agrupar miles. Es el texto que
 *  se edita cuando la columna NO tiene un formato configurado: `0.1` se escribe `0,1`. */
export function numeroEditable(n: number): string {
  return String(n).replace('.', SEP_DECIMAL)
}

/**
 * Inverso de `formatearExcel` para números: toma el texto tal como se ve y se escribe en la celda
 * —con los separadores configurados, el `%` y los literales del patrón (símbolo de moneda,
 * espacios, texto)— y devuelve el número que representa, o `null` si no hay ninguno.
 *
 * No intenta invertir el patrón carácter por carácter: se queda con los dígitos, el signo y los
 * separadores, y descarta el resto. Eso hace que valga tanto para lo que emitió el formateador
 * como para lo que teclea un usuario, que rara vez reescribe los literales.
 *
 * El `%` divide por 100 si lo trae el PATRÓN (lo que se ve ya está escalado ×100) o si lo escribió
 * el usuario; nunca las dos veces. Un patrón vacío sirve como modo genérico: respeta los
 * separadores sin escalar por su cuenta.
 */
export function parsearNumeroExcel(texto: string, patron: string): number | null {
  const bruto = texto.trim()
  if (bruto === '') return null
  const escapar = (c: string) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const permitido = new RegExp(`[^0-9+\\-${escapar(SEP_DECIMAL)}${escapar(SEP_MILES)}]`, 'g')
  let limpio = bruto.replace(permitido, '')
  limpio = limpio.split(SEP_MILES).join('')
  if (SEP_DECIMAL !== '.') limpio = limpio.split(SEP_DECIMAL).join('.')
  // Un solo signo, y solo adelante: `-1.234` es −1234, pero `1-2` no es un número.
  // Borrar los signos en vez de rechazarlos concatenaba los operandos y devolvía un número
  // plausible pero falso: `=1+1` daba 11 y `=2+3` daba 23, sin error ni aviso.
  const signos = limpio.match(/[+-]/g)
  if (signos && (signos.length > 1 || !/^[+-]/.test(limpio))) return null
  const negativo = limpio.startsWith('-')
  if (signos) limpio = limpio.slice(1)
  if (limpio === '' || limpio === '.') return null
  const n = Number(limpio)
  if (!Number.isFinite(n)) return null
  const escalas = escalasDe(patron) || (bruto.includes('%') ? 1 : 0)
  return (negativo ? -n : n) / Math.pow(100, escalas)
}

// ── API ──

export interface ResultadoFormato {
  texto: string
  /** Color de fuente declarado en la sección aplicada (`[Rojo]`), si hay alguno. */
  color?: string
}

/**
 * Aplica un patrón de Excel a un número o una fecha.
 * Ante un patrón vacío o inválido devuelve una representación razonable en lugar de fallar.
 */
export function formatearExcel(valor: number | Date, patron: string): string {
  return formatearExcelConColor(valor, patron).texto
}

/**
 * Formatea con un separador decimal distinto del configurado, sin tocar el de la app. Lo usa la
 * vista previa de Configuración, que tiene que mostrar el resultado del separador que el usuario
 * está eligiendo ANTES de guardarlo. Es un override de alcance acotado: se restaura siempre, y al
 * ser todo sincrónico no hay reentrada posible.
 */
export function formatearExcelConSeparador(
  valor: number | Date,
  patron: string,
  decimal: '.' | ',',
): ResultadoFormato {
  const previoDecimal = SEP_DECIMAL
  const previoMiles = SEP_MILES
  establecerSeparadores(decimal)
  try {
    return formatearExcelConColor(valor, patron)
  } finally {
    SEP_DECIMAL = previoDecimal
    SEP_MILES = previoMiles
  }
}

/** Igual que `formatearExcel`, pero además devuelve el color de fuente de la sección. */
export function formatearExcelConColor(valor: number | Date, patron: string): ResultadoFormato {
  if (valor instanceof Date) {
    if (!patron.trim()) return { texto: valor.toLocaleDateString(LOCALE_FORMATO) }
    const seccion = dividirSecciones(patron)[0] ?? ''
    const color = colorDeSeccion(seccion)
    return color === undefined
      ? { texto: formatearFechaSeccion(valor, seccion) }
      : { texto: formatearFechaSeccion(valor, seccion), color }
  }

  const n = Number.isFinite(valor) ? valor : 0
  const secciones = dividirSecciones(patron)
  if (!patron.trim()) return { texto: String(n) }

  // Excel: 1 sección → todo; 2 → [>=0, <0]; 3+ → [>0, <0, 0].
  let seccion: string
  let signo = ''
  // `?? ''` on each read: `secciones` is split from user-entered pattern text,
  // so indexing it is only safe after the length checks that already guard each
  // branch. Stating it here keeps the fallbacks total without an assertion.
  if (n > 0) seccion = secciones[0] ?? ''
  else if (n < 0) {
    if (secciones.length >= 2) seccion = secciones[1] ?? ''
    else { seccion = secciones[0] ?? ''; signo = '-' }
  } else {
    seccion = (secciones.length >= 3 ? secciones[2] : secciones[0]) ?? ''
  }
  if (!seccion.trim()) return { texto: '' }
  // `color` is optional and `exactOptionalPropertyTypes` is on, so the key is
  // omitted rather than set to undefined when the section declares no colour.
  const color = colorDeSeccion(seccion)
  return color === undefined
    ? { texto: signo + formatearNumeroSeccion(n, seccion) }
    : { texto: signo + formatearNumeroSeccion(n, seccion), color }
}
